import { Box, Button, Chip, useMediaQuery, useTheme } from "@mui/material";
import { useClientPortalNavigate } from "hooks/useClientPortalRouter";
import { AppointmentType, UpcomingAppointmentDto } from "types/generated/models";
import { getChipColor, getDateTimeLabel } from "./AppointmentItem.helpers";
import Row from "../../Shared/Row";
import { Typography } from "@powerdiary/components-library/lib";
import styles from "./AppointmentItem.module.css";
import { useAnalytics } from "hooks/useAnalytics";
import AppointmentActionButton from "./AppointmentActionButton";
import { useClientPortalConfigContext } from "context/ClientPortalConfig.ctx";
import { getGroupBookingLabel } from "../../../utils/groupBookingLabel";
import { getServiceValue } from "../../../utils/serviceValue";

interface IAppointmentItemProps {
    appointment: UpcomingAppointmentDto;
    requestAppointmentCancellation: (appointmentId: number) => void;
    requestAppointmentReschedule: (appointment: UpcomingAppointmentDto) => void;
}

const BUTTON_WIDTH = "200px";

export default function AppointmentItem({
    appointment,
    requestAppointmentCancellation,
    requestAppointmentReschedule,
}: IAppointmentItemProps) {
    const navigateTo = useClientPortalNavigate();
    const { track } = useAnalytics();
    const {
        clientPortalConfig: { groupBookingLabel },
    } = useClientPortalConfigContext();

    const joinTelehealthCall = (telehealthCallUrl: string) => {
        track("telehealth_join_clicked", {
            appointment_id: appointment.id,
            provider: appointment.provider,
        });
        navigateTo(telehealthCallUrl);
    };

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { date, time } = getDateTimeLabel(appointment.startOffset);
    const isGroupAppointment = appointment.appointmentType === AppointmentType.GroupAppointment;
    const serviceLabel = isGroupAppointment ? getGroupBookingLabel(groupBookingLabel) : "Service";
    const serviceValue = isGroupAppointment
        ? getServiceValue(appointment.groupDescription, appointment.service)
        : getServiceValue(appointment.service);

    return (
        <Box
            sx={{
                border: "1px solid #D4D4D9",
                borderRadius: "4px",
                padding: "24px",
                width: "100%",
                minWidth: 0,
            }}
            data-test-id={`section_appointment_item_${appointment.id}`}
        >
            <Box sx={{ display: "flex", flexDirection: "row", gap: "8px", marginBottom: "8px" }}>
                <Typography variant="paragraphLargeBold" data-test-id="date_time_label">
                    {date}
                    <Typography component="span" variant="paragraphLargeRegular">
                        {` at ${time}`}
                        <Chip
                            sx={{ marginLeft: "8px" }}
                            label={appointment.status}
                            color={getChipColor(appointment.status)}
                            size="small"
                            data-test-id="status_chip"
                        />
                    </Typography>
                </Typography>
            </Box>
            <Box
                sx={{
                    flexDirection: isMobile ? "column" : "row",
                    display: "flex",
                    gap: isMobile ? "24px" : "4px",
                    overflow: "hidden",
                    minWidth: 0,
                }}
            >
                <Box
                    className={styles.flexContainer}
                    sx={isMobile ? { width: "100%", minWidth: 0, overflow: "hidden" } : { flex: "1 1 0", minWidth: 0 }}
                >
                    <Row
                        subtitle="Location"
                        value={appointment.location}
                        dataTestIdSuffix={`location_${appointment.id}`}
                    />
                    <Row
                        subtitle="Client"
                        value={appointment.clientName}
                        dataTestIdSuffix={`client_${appointment.id}`}
                    />
                </Box>
                <Box
                    className={styles.flexContainer}
                    sx={isMobile ? { width: "100%", minWidth: 0, overflow: "hidden" } : { flex: "1 1 0", minWidth: 0 }}
                >
                    <Row
                        subtitle="Provider"
                        value={appointment.provider}
                        dataTestIdSuffix={`provider_${appointment.id}`}
                    />
                    <Row subtitle={serviceLabel} value={serviceValue} dataTestIdSuffix={`service_${appointment.id}`} />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                        alignItems: isMobile ? "flex-start" : "flex-end",
                        gap: "8px",
                    }}
                >
                    {appointment.isTelehealthEnabled && (
                        <Button
                            size="extraSmall"
                            variant="contained"
                            type="button"
                            onClick={() => joinTelehealthCall(appointment.participantUrl)}
                            sx={{ width: isMobile ? "100%" : BUTTON_WIDTH }}
                            data-test-id={`join_telehealth_call_button_${appointment.id}`}
                        >
                            Join Telehealth Call
                        </Button>
                    )}

                    <AppointmentActionButton
                        label="Reschedule"
                        onClick={() => requestAppointmentReschedule(appointment)}
                        dataTestId={`reschedule_appointment_button_${appointment.id}`}
                        status={appointment.rescheduleButtonStatus}
                        tooltipText={appointment.rescheduleButtonTooltipText}
                    />
                    <AppointmentActionButton
                        label="Cancel Appointment"
                        dataTestId={`cancel_appointment_button_${appointment.id}`}
                        onClick={() => requestAppointmentCancellation(appointment.id)}
                        status={appointment.cancelButtonStatus}
                        tooltipText={appointment.cancelButtonTooltipText}
                    />
                </Box>
            </Box>
        </Box>
    );
}
